
import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Button } from '@mui/material';

export interface ProductiveVocabularyWord {
  word: string;
  difficulty: string;
}

const ENGLISH_WORDS: Record<string, string[]> = {
  "1": ["boil", "duckling", "cupboard", "workbench", "agreeable", "swear", "cap", "unwire", "guitarist", "hardcover", "broccoli", "unsolved", "coral", "gladiator", "monkey", "jobless", "capitalization", "selfish", "emptiness", "pain", "brainpower", "lettuce", "wartime", "newsboy", "equipment", "sensation", "burglar", "idiotic", "spoils", "belong", "greeter", "screech", "tardy", "viewing", "heating", "homeward", "payment", "refereeing", "canyon", "nutmeg", "birdcage", "killer", "waistband", "mutter", "specific", "clerk", "orphaned", "bat", "bakeshop", "hilarious", "cheeseburger", "able", "morning", "tradition", "jean", "merry", "sunset", "stand", "sportsmanlike", "superstitious", "weightless", "vibration", "spotless", "plank", "pebble", "wastebasket", "envy", "hammer", "sun", "whole", "faucet", "cursive", "tire", "perch", "invitation", "continent", "membership", "scramble", "wood", "pheasant"],
  "2": ["anonymous", "strategic", "polling", "combustion", "remembrance", "obscure", "cleric", "outrank", "absolution", "priesthood", "critic", "uncensored", "hobbyist", "perilous", "irate", "global", "hovel", "sophomore", "dirham", "keen", "fuddle", "battlewagon", "entail", "scarab", "dank", "guarantee", "adjuster", "enliven", "biodegradable", "enfold", "amputee", "trench", "ovation", "unsuitable", "suburban", "memento", "inevitable", "marine", "uncorrupted", "mystique", "behemoth", "marshland", "unaltered", "minority", "illicit", "stockbroker", "luster", "contort", "crimping", "finery", "feminist", "hallmark", "showy", "tarantella", "restaurateur", "uncanny", "kilter", "legislation", "illogical", "entirety", "irreparable", "itinerary", "concept", "quivering", "plagiarize", "effectiveness", "portrayal", "civilization", "variation", "omen", "teriyaki"],
  "3": ["oligarch", "compartmentalize", "lemma", "aquaplane", "homicidal", "editorialize", "inkling", "valency", "mesosphere", "distillate", "deduce", "deformation", "puerile", "cynical", "surreal", "denigrate", "unconventionality", "maceration", "jocularity", "poignant", "subornation", "heliotrope", "clef", "exudation", "inequitable", "lox", "loyalist", "immutable", "conga", "affirmation", "pejorative"]
};

const SPANISH_WORDS: Record<string, string[]> = {
  "1": ["mono", "dolor", "vacío", "lechuga", "animales", "nueva", "camión", "ensalada"],
  "2": ["hervir", "agradable", "sensación", "lavaplatos", "abundante", "avestruz", "modo", "zonas"],
  "3": ["concepto", "variación", "intelecto", "indiferencia", "perezoso", "conserjería", "itinerario", "comisión"]
};


const getDifficulty = (index: number): string => String(Math.floor(index / 2) + 1);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ProductiveVocabularyTask: React.FC<{ wordIndex: number, cohort: string, onWordChange: (word: ProductiveVocabularyWord) => void }> = ({ wordIndex, cohort, onWordChange }) => {
  const { i18n, t } = useTranslation(cohort);
  const language: string = i18n.language;
  const [words, setWords] = useState<ProductiveVocabularyWord[]>([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const WORDS = language === 'es-419' ? SPANISH_WORDS : ENGLISH_WORDS;
  const [_wordPool, setWordPool] = useState<Record<string, string[]>>({
    '1': [...WORDS['1']],
    '2': [...WORDS['2']],
    '3': [...WORDS['3']],
  });

  useEffect(() => {
    setCurrentWordIndex(wordIndex);
    setWords([]);
    setWordPool({
      '1': [...WORDS['1']],
      '2': [...WORDS['2']],
      '3': [...WORDS['3']],
    });
    pickWord(wordIndex, getDifficulty(wordIndex));
  }, [language, wordIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const randomWord = (words: string[], difficulty: string): ProductiveVocabularyWord => {
    if (!words || words.length === 0) {
      return { word: '', difficulty };
    }

    const idx = Math.floor(Math.random() * words.length);
    const word = words[idx];

    return { word, difficulty };
  };

  const resolvePool = (pool: Record<string, string[]>, difficulty: string): { array: string[], difficulty: string } => {
    let array = pool[difficulty] ?? [];

    if (array.length === 0) {
      if (difficulty === '1') {
        pool['1'] = [...WORDS['1']];
        array = pool['1'];
      } else if (difficulty === '2') {
        difficulty = '1';
        array = pool['1'];
      } else if (difficulty === '3') {
        difficulty = '2';
        if (pool['2'].length === 0) {
          difficulty = '1';
        }
        array = pool[difficulty];
      }
    }

    return { array, difficulty };
  };

  const pickWord = (index: number, desiredDifficulty: string) => {
    setWordPool((prevPool) => {
      const nextPool = { ...prevPool };

      const { array, difficulty } = resolvePool(nextPool, desiredDifficulty);
      const pvWord = randomWord(array, difficulty);

      nextPool[difficulty] = array.filter((w) => w !== pvWord.word);

      setWords((prevWords) => {
        const newWords = [...prevWords];
        if (index === prevWords.length) {
          newWords.push(pvWord);
        } else {
          newWords[index] = pvWord;
        }

        return newWords;
      });

      return nextPool;
    });
  };

  const handleDontKnowWord = () => {
    pickWord(currentWordIndex, getDifficulty(currentWordIndex));
  };

  const wordObj = words[currentWordIndex];

  useEffect(() => {
    if (wordObj && wordObj.word) {
      onWordChange(wordObj);
    }
  }, [wordObj, onWordChange]);

  return (
    <Box sx={{ pt: 2 }}>
      {currentWordIndex > -1 && wordObj && (
        <Box
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            p: 2.5,
            background: 'linear-gradient(90deg, rgba(26, 161, 235, 0.2) 0%, rgba(189, 0, 255, 0.2) 100%)'
          }}
        >
          <Typography
            variant="h3"
            component='div'
            align="center"
            sx={{ padding: 3 }}
            aria-live="polite"
            aria-atomic="true"
          >
            {wordObj.word}
          </Typography>
          <Button
            onClick={handleDontKnowWord}
            aria-label={t("I don't know this word, next")}
          >
            {t("I don't know this word, next")}
          </Button>
        </Box>
      )}
    </Box>
  );
};


